import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { FormGroup, FormBuilder, AbstractControl } from '@angular/forms';
import { DtoArticleSalesgroup } from './models/DtoArticleSalesgroup';
import { DtoArticleSubSalesgroup } from './models/DtoArticleSubSalesgroup';
import { ArticleSubSalesgroupComponent } from './article-subsalesgroup.component';
import { DialogService } from '@progress/kendo-angular-dialog';
import { PageSubheaderBarConfig } from '../../../fw/pages/page-config-api';
import { SettingsBaseService } from './settings-base.service';
import { FormService } from '../../../fw/services/form.service';
import { ValidationMessageComponent } from '../../shared/validation-message.component';
import { SharedValidators } from '../../shared/shared-validators';
import { SharedService } from '../../shared/shared.service';
import { Observable } from 'rxjs/Rx';
import { AutoUnsubscribe } from '../../../fw/decoractors/AutoUnsubscribe';
import {GridDataResult, SelectableSettings} from "@progress/kendo-angular-grid";
import {orderBy, SortDescriptor} from "@progress/kendo-data-query";
import {AsyncValidatorWrapper} from "../../../fw/helpers/async-validator-wrapper";

@Component({
    selector: 'article-salesgroup',
    template: require('./article-salesgroup.component.html'),
    styles: [require('./article-salesgroup.component.css')]
})
@AutoUnsubscribe()

export class ArticleSalesgroupComponent implements OnInit {
    @Input() dialog: DialogRef;
    @Input() id: number;
    @Input() copyFromId: number;
    @ViewChild('validationMessage', {static: false}) validationMessage: ValidationMessageComponent;
    pageSubheaderBarConfig: PageSubheaderBarConfig;
    gridView: GridDataResult;
    form: FormGroup;
    dtoObject: DtoArticleSalesgroup;
    activeItems: Array<{ text: string, value: boolean }> = [
        { text: "Ja", value: true },
        { text: "Nee", value: false }
    ];
    selectedSubgroupIds: number[] = [];
    selectableSettings: SelectableSettings = {
        checkboxOnly: false,
        enabled: true,
        mode: "single"
    };

    constructor(private fb: FormBuilder,
                private fs: FormService,
                private ds: SettingsBaseService,
                private ss: SharedService,
                private dialogService: DialogService) { }

    ngOnInit() {
        this.pageSubheaderBarConfig = {
            title: 'SUBGROEPEN',
            buttonDefinition: [
                {
                    clickEventType: 'create',
                    description: 'TOEVOEGEN',
                    isSmall: true
                },
                {
                    clickEventType: 'copy',
                    description: 'KOPIËREN',
                    isSmall: true
                },
                {
                    clickEventType: 'edit',
                    description: 'WIJZIGEN',
                    isSmall: true
                },
                {
                    clickEventType: 'delete',
                    description: 'VERWIJDEREN',
                    isSmall: true
                }
            ]
        };

        this.form = this.fb.group({
            description: ['', [SharedValidators.customRequired]],
            index: [null,[SharedValidators.customRequired], [AsyncValidatorWrapper.debounce(this.validateIndex.bind(this))]],
            isActive: true
        });

        this.form.valueChanges.subscribe(() =>
            {if (this.validationMessage) this.validationMessage.getFormValidationErrors();}
        );

        if (this.id > 0) {
            this.ds.getArticleSalesgroupById(this.id)
                .subscribe(response => {
                    this.dtoObject = response;
                    this.fs.patchDtoToForm(this.dtoObject, this.form);
                    this.loadSubgroups();
                });
        }
        else {
            this.ds.getArticleSalesgroupNextIndex()
                .subscribe(res => {
                    this.dtoObject = {
                        id: 0,
                        isActive: true,
                        index: res,
                        articleSubSalesgroups: []
                    };
                    if (this.copyFromId > 0) {
                        this.ds.getArticleSalesgroupById(this.copyFromId)
                            .subscribe(response => {
                                this.dtoObject.description = response.description;
                                this.dtoObject.isActive = response.isActive;
                                this.fs.patchDtoToForm(this.dtoObject, this.form);
                                this.loadSubgroups();
                            });
                    }
                    else {
                        this.fs.patchDtoToForm(this.dtoObject, this.form);
                        this.loadSubgroups();
                    }
                });
        }
    }

    validateIndex(control: AbstractControl) {
        return Observable.create(observer => {
            if (this.dtoObject != null && control.dirty && control.value != null) {
                this.ds.checkArticleSalesgroupIndexAvailable(this.dtoObject.id, control.value)
                    .subscribe((response: boolean) => {
                        observer.next(response ? null : { indexNotAvailable: true });
                        observer.complete();
                        if (this.validationMessage) this.validationMessage.getFormValidationErrors();
                    });
            }
            else {
                observer.next(null);
                observer.complete();
                if (this.validationMessage) this.validationMessage.getFormValidationErrors();
            }
        });
    }

    btnCancelClicked(event) {
        this.closeWindow(false);
    }

    btnSaveClicked(event) {
        this.validationMessage.validateForm();

        if (this.form.valid) {
            let result = this.fs.patchFormToDto(this.dtoObject, this.form);
            this.ds.saveArticleSalesgroup(result)
                .subscribe(response => {
                    if (!response.hasError) {
                        this.closeWindow(true);
                    }
                    else {
                        this.ss.message(response.errorMessage);
                    }
                });
        }
    }

    btnSubHeaderBarClicked(event) {
        if (event == 'create') {
            this.showSubSalesgroupsEditWindow(null, null);
        } else if (event == 'edit') {
            if (this.getSelectedSubgroup()) {
                this.showSubSalesgroupsEditWindow(this.getSelectedSubgroup(), null);
            }
        } else if (event == 'copy') {
            if (this.getSelectedSubgroup()) {
                this.showSubSalesgroupsEditWindow(null, this.getSelectedSubgroup());
            }
        } else if (event == 'delete') {
            if (this.getSelectedSubgroup()) {
                this.ss.confirm('Weet u zeker dat u dit record wilt verwijderen?')
                    .subscribe((res: boolean) => {
                        if (res) {
                            this.dtoObject.articleSubSalesgroups.splice(this.dtoObject.articleSubSalesgroups.indexOf(this.getSelectedSubgroup()), 1);
                            this.loadSubgroups();
                        }
                    });
            }
        }
    }

    showSubSalesgroupsEditWindow(subGroupObject: DtoArticleSubSalesgroup, copyFromObject: DtoArticleSubSalesgroup) {
        const dialogRef = this.dialogService.open({
            content: ArticleSubSalesgroupComponent
        });

        let isNewObject = false;
        if (!subGroupObject) {
            subGroupObject = {
                id: 0,
                isActive: true,
                index: 0
            };
            isNewObject = true;
        }

        const dialogContent: ArticleSubSalesgroupComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.parentId = this.dtoObject.id;
        dialogContent.dtoObject = subGroupObject;
        dialogContent.copyFromObject = copyFromObject;
        dialogContent.currentObjects = this.dtoObject.articleSubSalesgroups;
        dialogContent.isNewObject = isNewObject;
        dialogContent.subgroupSaved
            .subscribe((result: DtoArticleSubSalesgroup) => {
                if (isNewObject) {
                    this.dtoObject.articleSubSalesgroups.push(result);
                } else {
                    let currentIndex = this.dtoObject.articleSubSalesgroups.indexOf(subGroupObject);
                    if (this.dtoObject.articleSubSalesgroups[currentIndex]) {
                        this.dtoObject.articleSubSalesgroups[currentIndex] = result;
                    }
                }
                this.loadSubgroups();
            });
    }

    loadSubgroups() {
        let sort:  SortDescriptor[] = [{
            dir: "asc",
            field: "index"
        }];
        this.gridView = {
            data: orderBy(this.dtoObject.articleSubSalesgroups, sort),
            total: this.dtoObject.articleSubSalesgroups.length
        };
        this.selectedSubgroupIds = [];
    }

    getSelectedSubgroup(): DtoArticleSubSalesgroup {
        if (this.selectedSubgroupIds.length == 1) {
            return this.dtoObject.articleSubSalesgroups[this.dtoObject.articleSubSalesgroups.findIndex(x => x.id == this.selectedSubgroupIds[0])];
        }
        return null;
    }

    rowdoubleClicked() {
        if (this.getSelectedSubgroup()) {
            this.showSubSalesgroupsEditWindow(this.getSelectedSubgroup(), null);
        }
    }

    closeWindow(result: boolean) {
        if (this.dialog) {
            this.dialog.dialog.instance.action.emit(result);
            this.dialog.close();
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-base/article-salesgroup.component.ts